import { WinCondition, type GameState, type Player } from '@susdle/shared';
import type { SeatPlayer } from '../shared/components/AvatarFrame.js';
import type { RevealedRole } from '../discussion/types.js';
import type {
  GameEndOutcome,
  GameEndPlayer,
  GameEndView,
  GameEndWord,
  WinningSide,
} from '../game-end/types.js';
import { classifyAvatarUrl } from './stateToView.js';

export interface GameEndMappingContext {
  meId: string;
}

/**
 * GameEnd mapper — a thin projection of terminal state. Reads `revealedRole`
 * (granted to all clients by endGameWith's fanOutVisibility) and the public
 * `state.revealedSecretWord` field set at endGameWith. Mapper does not read
 * `player.role` (SELF-tagged) nor a villager's `secretWord` (SELF-tagged);
 * the server publishes both at GameEnd via the channels above.
 */

function seatFromPlayer(player: Player): SeatPlayer {
  const avatar = classifyAvatarUrl(player.avatarUrl);
  const seat: SeatPlayer = {
    id: player.id,
    name: player.name,
    avatarUrl: player.avatarUrl,
    isDefaultAvatar: avatar.isDefaultAvatar,
  };
  if (avatar.defaultAvatarIndex !== undefined) seat.defaultAvatarIndex = avatar.defaultAvatarIndex;
  return seat;
}

/** Which side the win condition belongs to. Stalemate / None → 'none'. */
function winningSide(win: WinCondition): WinningSide {
  if (win === WinCondition.VillageElimination) return 'village';
  if (win === WinCondition.GhostsByParity || win === WinCondition.GhostsByGuess) return 'ghosts';
  return 'none';
}

function outcomeOf(win: WinCondition): GameEndOutcome {
  switch (win) {
    case WinCondition.VillageElimination:
      return 'villageElimination';
    case WinCondition.GhostsByParity:
      return 'ghostsByParity';
    case WinCondition.GhostsByGuess:
      return 'ghostsByGuess';
    default:
      return 'stalemate';
  }
}

/** Winning-aligned for the resolved side. Village side = villagers + fools. */
function isWinner(revealedRole: RevealedRole, side: WinningSide): boolean {
  if (side === 'village') return revealedRole !== 'ghost';
  if (side === 'ghosts') return revealedRole === 'ghost';
  return false; // stalemate — everyone loses
}

export function stateToGameEndView(state: GameState, ctx: GameEndMappingContext): GameEndView {
  const win = state.winCondition;
  const side = winningSide(win);

  const cast: GameEndPlayer[] = [...state.players.values()]
    .sort((a, b) => a.turnOrder - b.turnOrder)
    .map((p) => {
      const revealedRole = (p.revealedRole || 'villager') as RevealedRole;
      return {
        ...seatFromPlayer(p),
        turnOrder: p.turnOrder,
        role: revealedRole,
        won: isWinner(revealedRole, side),
        eliminated: !p.alive,
      };
    });

  /* The word is shown UNLESS it was already revealed publicly — which happens
     ONLY on a correct guess (GuessReveal showed the guessed word, and a correct
     guess === the secret). A WRONG guess showed a different word, so the real
     answer is still new and must show; same for parity / stalemate / AFK win. */
  const wordAlreadyRevealed = win === WinCondition.GhostsByGuess;
  const word: GameEndWord = wordAlreadyRevealed
    ? { shown: false }
    : { shown: true, word: state.revealedSecretWord };

  const host = state.players.get(state.hostId);

  return {
    meId: ctx.meId,
    side,
    outcome: outcomeOf(win),
    cast,
    guess: state.ghostGuessText,
    word,
    isHost: state.hostId === ctx.meId,
    hostName: host?.name ?? '',
  };
}
